import React, { useState, useRef, useEffect } from 'react';

const OrderStatusSelector = ({ status, onChange, options, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [dropDirection, setDropDirection] = useState('down');

  // Find selected status option
  const selectedOption = options.find(option => option.value === status) || options[0];

  // Calculate dropdown direction and handle positioning
  const calculateDirection = () => {
    if (!buttonRef.current) return;
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const windowHeight = window.innerHeight;
    const spaceBelow = windowHeight - buttonRect.bottom;
    
    // Need more space for dropdown - at least 220px
    const shouldOpenUp = spaceBelow < 220;
    setDropDirection(shouldOpenUp ? 'up' : 'down');
    
    // After render, adjust position if needed
    setTimeout(() => {
      if (!menuRef.current) return;
      
      const menuRect = menuRef.current.getBoundingClientRect();
      
      // If dropdown is cut off at bottom of window, force it upward
      if (menuRect.bottom > windowHeight - 20) {
        setDropDirection('up');
      }
      
      // If dropdown is cut off at top of window when opening upward, force it downward
      if (dropDirection === 'up' && menuRect.top < 20) {
        setDropDirection('down');
        
        // If it would still be cut off downward, position it at top of screen with scrolling
        if (menuRect.height > windowHeight - buttonRect.bottom) {
          menuRef.current.style.maxHeight = `${windowHeight - buttonRect.bottom - 20}px`;
        }
      }
    }, 0);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', calculateDirection);
    
    // Initial calculation
    if (isOpen) {
      calculateDirection();
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', calculateDirection); // <-- Fixed this line
    };
  }, [isOpen]); // Added isOpen as dependency to recalculate when opened

  // Toggle dropdown
  const toggleDropdown = () => {
    const newIsOpen = !isOpen;
    setIsOpen(newIsOpen);
    
    // If opening, calculate direction
    if (newIsOpen) {
      calculateDirection();
    }
  };

  // Get status color based on selected option
  const getStatusColor = (colorName) => {
    const colorMap = {
      'yellow': 'bg-yellow-100 border-yellow-500 text-yellow-800',
      'blue': 'bg-blue-100 border-blue-500 text-blue-800',
      'green': 'bg-green-100 border-green-500 text-green-800',
      'red': 'bg-red-100 border-red-500 text-red-800',
    };
    return colorMap[colorName] || 'bg-gray-100 border-gray-500 text-gray-800';
  };
  
  // Get dot color based on option
  const getDotColor = (colorName) => {
    const colorMap = {
      'yellow': 'bg-yellow-500',
      'blue': 'bg-blue-500',
      'green': 'bg-green-500',
      'red': 'bg-red-500',
    };
    return colorMap[colorName] || 'bg-gray-500';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
        Statut de la commande <span className="text-red-500 ml-1">*</span>
      </label>
      
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleDropdown}
        className={`relative w-full bg-white border ${error ? 'border-red-300' : 'border-gray-300'} rounded-md shadow-sm pl-3 pr-10 py-2.5 text-left cursor-default focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540] sm:text-sm`}
      >
        <span className="flex items-center">
          <span className={`flex-shrink-0 inline-block h-2.5 w-2.5 rounded-full ${getDotColor(selectedOption.color)} mr-2`}></span>
          <span className="block truncate">{selectedOption.label}</span>
        </span>
        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fillRule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        </span>
      </button>

      {isOpen && (
        <div 
          ref={menuRef}
          className={`fixed z-50 w-full bg-white shadow-lg rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm`}
          style={{ 
            maxHeight: '15rem',
            minWidth: buttonRef.current ? buttonRef.current.offsetWidth + 'px' : 'auto',
            ...(dropDirection === 'up' 
              ? { bottom: window.innerHeight - (buttonRef.current?.getBoundingClientRect().top || 0) + 'px' } 
              : { top: (buttonRef.current?.getBoundingClientRect().bottom || 0) + 'px' }),
            left: buttonRef.current ? buttonRef.current.getBoundingClientRect().left + 'px' : '0',
          }}
        >
          {options.map((option) => (
            <div
              key={option.value}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-gray-100 ${
                option.value === status ? 'bg-gray-50' : ''
              }`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              <div className="flex items-center">
                <span className={`flex-shrink-0 inline-block h-2.5 w-2.5 rounded-full ${getDotColor(option.color)} mr-2`}></span>
                <span className={`font-normal block truncate ${option.value === status ? 'font-semibold' : ''}`}>
                  {option.label}
                </span>
              </div>

              {option.value === status && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#174540]">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
    </div>
  );
};

export default OrderStatusSelector;